import React from 'react';
import {
  formatLocalDate,
  formatLocalTime,
  getBookingEndDate,
  getBookingStartDate,
} from './bookingUtils';
import './MobileBookingDayAgenda.css';

const START_HOUR = 7;
const END_HOUR = 24;

export default function MobileBookingDayAgenda({
  selectedDate,
  selectedDayBookings,
  aircraftList,
  usersMap,
  trainingOptions,
  openBookingModal,
  openBookingDetails,
  changeDayView,
}) {
  function moveDay(offset) {
    const date = new Date(`${selectedDate}T12:00:00`);
    if (!Number.isFinite(date.getTime())) return;
    date.setDate(date.getDate() + offset);
    changeDayView(formatLocalDate(date));
  }

  return (
    <div className="mobile-operations-board">
      <div className="mobile-operations-board__day-nav">
        <button type="button" aria-label="Previous day" onClick={() => moveDay(-1)}>
          ‹
        </button>
        <div>
          <div className="mobile-operations-board__kicker">Operations board</div>
          <h3>{formatSelectedDate(selectedDate)}</h3>
        </div>
        <button type="button" aria-label="Next day" onClick={() => moveDay(1)}>
          ›
        </button>
      </div>

      {aircraftList.length === 0 ? (
        <div className="mobile-operations-board__empty">No aircraft available.</div>
      ) : (
        <div className="mobile-operations-board__aircraft-list">
          {aircraftList.map((aircraft) => {
            const bookings = getAircraftBookingsForDay(
              selectedDayBookings,
              aircraft.id,
              selectedDate
            );
            const freePeriods = getFreePeriods(bookings, selectedDate);
            const scheduleItems = getScheduleItems(bookings, freePeriods);

            return (
              <section className="mobile-aircraft-card" key={aircraft.id}>
                <header className="mobile-aircraft-card__header">
                  <div>
                    <h4>{aircraft.registration}</h4>
                    <div className="mobile-aircraft-card__type">
                      {aircraft.icao_type || 'Aircraft'}
                    </div>
                  </div>
                  <span className={`mobile-aircraft-card__status mobile-aircraft-card__status--${getStatusKey(aircraft.status)}`}>
                    {getAircraftStatusLabel(aircraft.status)}
                  </span>
                </header>

                <div className="mobile-aircraft-card__schedule">
                  {scheduleItems.map((item) => {
                    if (item.type === 'booking') {
                      const booking = item.booking;
                      const pilot = usersMap[booking.user_id];
                      const student = usersMap[booking.student_user_id];
                      const instructor = usersMap[booking.instructor_user_id];
                      const course = trainingOptions?.enrolments?.find(
                        (enrolment) => String(enrolment.id) === String(booking.course_enrolment_id)
                      )?.training_courses;
                      const route = getBookingRoute(booking);

                      return (
                        <button
                          key={`booking-${booking.id}`}
                          type="button"
                          className="mobile-schedule-row mobile-schedule-row--booked"
                          onClick={() => openBookingDetails(booking)}
                        >
                          <span className="mobile-schedule-row__time">
                            {formatBookingTime(item.start, item.end, selectedDate)}
                          </span>
                          <span className="mobile-schedule-row__detail">
                            {booking.booking_type === 'training'
                              ? `${student?.full_name || student?.email || 'Student'} · Instructor: ${instructor?.full_name || instructor?.email || 'Instructor'}${course ? ` · ${[course.code, course.name].filter(Boolean).join(' · ')}` : ''}`
                              : `${pilot?.full_name || pilot?.email || 'Booked'}${route ? ` · ${route}` : ''}`}
                          </span>
                          <span className="mobile-schedule-row__state">
                            {booking.booking_type === 'training' ? 'TRAINING' : 'Booked'}
                          </span>
                        </button>
                      );
                    }

                    const period = item.period;
                    return (
                      <div className="mobile-schedule-row mobile-schedule-row--free" key={`${aircraft.id}-${period.startTime}`}>
                        <div>
                          <span className="mobile-schedule-row__time">
                            {period.startTime}–{period.endTime}
                          </span>
                          <span className="mobile-schedule-row__detail">Available</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => openBookingModal(
                            aircraft.id,
                            selectedDate,
                            null,
                            period.startTime
                          )}
                        >
                          Book
                        </button>
                      </div>
                    );
                  })}

                  {freePeriods.length === 0 && bookings.length > 0 && (
                    <div className="mobile-aircraft-card__full">No free time between 07:00 and 24:00.</div>
                  )}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function getAircraftBookingsForDay(bookings, aircraftId, selectedDate) {
  const boardStart = makeLocalDateAtHour(selectedDate, START_HOUR);
  const boardEnd = makeLocalDateAtHour(selectedDate, END_HOUR);

  return (bookings || [])
    .filter((booking) => {
      if (String(booking.aircraft_id) !== String(aircraftId)) return false;
      const start = getBookingStartDate(booking);
      const end = getBookingEndDate(booking);
      return Boolean(start && end && boardStart && boardEnd && start < boardEnd && end > boardStart);
    })
    .sort((left, right) => {
      const leftStart = getBookingStartDate(left);
      const rightStart = getBookingStartDate(right);
      return (leftStart?.getTime() || 0) - (rightStart?.getTime() || 0);
    });
}

export function getFreePeriods(bookings, selectedDate) {
  const boardStart = makeLocalDateAtHour(selectedDate, START_HOUR);
  const boardEnd = makeLocalDateAtHour(selectedDate, END_HOUR);
  if (!boardStart || !boardEnd) return [];

  const occupied = [...(bookings || [])]
    .map((booking) => {
      const start = getBookingStartDate(booking);
      const end = getBookingEndDate(booking);
      if (!start || !end) return null;
      const clippedStart = start < boardStart ? boardStart : start;
      const clippedEnd = end > boardEnd ? boardEnd : end;
      return clippedEnd > clippedStart ? { start: clippedStart, end: clippedEnd } : null;
    })
    .filter(Boolean)
    .sort((left, right) => left.start - right.start);

  const merged = [];
  occupied.forEach((period) => {
    const previous = merged[merged.length - 1];
    if (previous && period.start <= previous.end) {
      if (period.end > previous.end) previous.end = period.end;
      return;
    }
    merged.push({ ...period });
  });

  const free = [];
  let cursor = boardStart;
  merged.forEach((period) => {
    if (period.start > cursor) free.push(createPeriod(cursor, period.start));
    if (period.end > cursor) cursor = period.end;
  });
  if (cursor < boardEnd) free.push(createPeriod(cursor, boardEnd));
  return free;
}

function getScheduleItems(bookings, freePeriods) {
  const bookingItems = bookings.map((booking) => {
    const start = getBookingStartDate(booking);
    const end = getBookingEndDate(booking);
    return {
      type: 'booking',
      booking,
      start,
      end,
      order: Math.max(START_HOUR * 60, start.getHours() * 60 + start.getMinutes()),
    };
  });
  const freeItems = freePeriods.map((period) => ({
    type: 'free',
    period,
    order: clockToMinutes(period.startTime),
  }));
  return [...bookingItems, ...freeItems].sort((left, right) => left.order - right.order);
}

function clockToMinutes(value) {
  const [hours, minutes] = String(value || '').split(':').map(Number);
  return hours * 60 + minutes;
}

function createPeriod(start, end) {
  return {
    startTime: formatLocalTime(start),
    endTime: end.getHours() === 0 && end.getDate() !== start.getDate()
      ? '24:00'
      : formatLocalTime(end),
  };
}

function makeLocalDateAtHour(selectedDate, hour) {
  const date = new Date(`${selectedDate}T00:00:00`);
  if (!Number.isFinite(date.getTime())) return null;
  date.setHours(hour, 0, 0, 0);
  return date;
}

function formatSelectedDate(value) {
  const date = new Date(`${value}T12:00:00`);
  if (!Number.isFinite(date.getTime())) return 'Select a day';
  return date.toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function formatBookingTime(start, end, selectedDate) {
  if (!start || !end) return 'Booked';
  const startsToday = formatLocalDate(start) === selectedDate;
  const endsToday = formatLocalDate(end) === selectedDate;
  if (startsToday && endsToday) return `${formatLocalTime(start)}–${formatLocalTime(end)}`;
  if (startsToday) return `${formatLocalTime(start)}–24:00`;
  if (endsToday) return `07:00–${formatLocalTime(end)}`;
  return '07:00–24:00';
}

function getBookingRoute(booking) {
  const briefing = booking?.briefing_data || {};
  const flightType = String(briefing.flightType || briefing.flight_type || '').trim().toLowerCase();
  if (flightType === 'local') return 'Local';
  if (flightType === 'circuits') return 'Circuits';
  const destination = String(briefing.destinationAirfield || briefing.destination_airfield || '').trim().toUpperCase();
  return destination || (flightType === 'land-away' ? 'Land Away' : '');
}

function getStatusKey(status) {
  const value = String(status || 'serviceable').toLowerCase();
  if (value === 'aog' || value === 'defect' || value === 'deferred') return value;
  return 'serviceable';
}

function getAircraftStatusLabel(status) {
  const value = getStatusKey(status);
  if (value === 'aog') return 'AOG';
  if (value === 'defect') return 'Defect';
  if (value === 'deferred') return 'Deferred';
  return 'Serviceable';
}